import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useQueryParams from "@/lib/hooks/useQueryParams";
import { Button } from "@/components/ui/button/Button";
import LoadingIndicator from "@/components/ui/loading/LoadingIndicator";
import { useToast } from "@/lib/hooks/useToast";
import { useUserStore } from "@/store/user/userStore";
import RoomManagementTable from "./components/RoomManagementTable";
import PICManagementTable from "./components/PICManagementTable";

const ManagementPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const userStore = useUserStore();
  const { params, updateParams } = useQueryParams();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(params.get("tab") || "Room");
  const hasRedirectedRef = useRef(false);

  useEffect(() => {
    if (!userStore.data || hasRedirectedRef.current) return;
    if (userStore.canAccessManagement) return;

    hasRedirectedRef.current = true;
    toast({
      title: "Forbidden",
      description: "You are not authorized to access Room Management.",
      variant: "destructive",
    });

    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate("/home", { replace: true });
  }, [navigate, toast, userStore.canAccessManagement, userStore.data]);

  const handleTabChange = (tabName: string) => {
    setActiveTab(tabName);
    updateParams({ tab: tabName, page: "1" });
  };

  if (!userStore.data || !userStore.canAccessManagement) {
    return null;
  }

  return (
    <div className="container mx-auto py-10 px-8 min-h-screen bg-background text-foreground">
      {loading && <LoadingIndicator loading={loading} />}

      <div className="text-xl font-lsemibold mb-6">System Management</div>

      <div className="flex gap-4 mb-6">
        <Button
          variant="ghost"
          className={`btn shadow-lg shadow-secondary px-4 py-2 rounded text-sm font-lmedium border-2 border-transparent transition-all ${activeTab === "Room"
              ? "border-primary bg-card text-primary"
              : "text-muted-foreground hover:text-foreground"
            }`}
          onClick={() => handleTabChange("Room")}
        >
          Room
        </Button>
        <Button
          variant="ghost"
          className={`btn shadow-lg shadow-secondary px-4 py-2 rounded text-sm font-lmedium border-2 border-transparent transition-all ${activeTab === "PIC"
              ? "border-primary bg-card text-primary"
              : "text-muted-foreground hover:text-foreground"
            }`}
          onClick={() => handleTabChange("PIC")}
        >
          PIC
        </Button>
      </div>

      <div className="animate-in fade-in duration-500">
        {activeTab === "Room" && <RoomManagementTable setLoading={setLoading} />}
        {activeTab === "PIC" && <PICManagementTable setLoading={setLoading} />}
      </div>
    </div>
  );
};

export default ManagementPage;
